import React, { useEffect, useState } from "react";
import { Download, X, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { registerDownloadModal } from "@/lib/exportHelpers";

/** Singleton modal opened after POST /exports/request succeeds.
 *  Mounted once at the app root.
 *
 *  Why a modal instead of an automatic download?
 *  --------------------------------------------
 *  When the app runs inside an iframe (e.g. the Emergent platform preview),
 *  Chrome may silently block downloads from the iframe, or pre-fetch the
 *  link (burning a single-use token) before the user actually clicks.
 *
 *  The modal sidesteps everything by giving the user THREE explicit options
 *  that all hit the same URL:
 *
 *    1. The primary "Click to download" button — a plain <a href download>
 *       without target=_blank, so it triggers a same-tab download.
 *    2. "Open in new tab" — explicit user-gesture popup, useful if their
 *       browser blocks iframe downloads.
 *    3. "Copy URL" — paste into a brand-new Chrome tab. 100% reliable
 *       fallback regardless of any extension / AV / iframe sandbox.
 *
 *  The backend token is reusable within its 120s expiry, so pre-fetches and
 *  retries are safe.
 */
export default function DownloadModal() {
  const [state, setState] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    registerDownloadModal((payload) => {
      setCopied(false);
      setState(payload);
    });
  }, []);

  if (!state) return null;

  const { url, count, mode, filename } = state;
  const close = () => setState(null);
  const modeLabel =
    mode === "new" ? "newly approved" : mode === "all" ? "approved" : "previously exported";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Download link copied — paste into a new Chrome tab");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      console.error("Clipboard write failed:", e);
      toast.error("Couldn't access clipboard — select the link below and copy manually");
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="download-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
            <Download className="w-4 h-4 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>
              Download ready
            </h3>
            <div className="text-xs text-slate-500 font-mono">
              {count} {modeLabel} question{count === 1 ? "" : "s"} · link valid for 2 minutes
            </div>
          </div>
          <button
            data-testid="download-modal-close"
            onClick={close}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          {/* Primary: same-tab download (no target, so no iframe issues) */}
          <a
            href={url}
            download={filename}
            data-testid="download-modal-link"
            onClick={() => setTimeout(close, 1500)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition shadow-sm"
          >
            <Download className="w-4 h-4" />
            Click to download
          </a>

          {/* Secondary: explicit new-tab open */}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            download={filename}
            data-testid="download-modal-newtab"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-200 hover:border-orange-300 hover:bg-orange-50 text-slate-700 text-sm font-semibold transition"
          >
            <ExternalLink className="w-4 h-4" />
            Open in new tab
          </a>

          <div className="pt-2">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Still nothing happens? Copy this link and paste into a fresh Chrome tab
            </div>
            <div className="flex items-stretch gap-2">
              <input
                readOnly
                value={url}
                onFocus={(e) => e.target.select()}
                data-testid="download-modal-url-field"
                className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs font-mono text-slate-700 outline-none focus:border-orange-400"
              />
              <button
                data-testid="download-modal-copy-btn"
                onClick={copy}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition ${
                  copied
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-900 hover:bg-slate-800 text-white"
                }`}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex justify-end">
          <button
            onClick={close}
            data-testid="download-modal-cancel"
            className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
